import React, { useState, useCallback } from 'react';
import { List } from 'react-window';
import { Autocomplete, TextField, CircularProgress, useTheme } from '@mui/material';
import { cities } from '../utils/cities-array';

const OptimizedCityAutocomplete = ({ value, onChange, sx = {}, ...props }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [inputValue, setInputValue] = useState('');
  const [filteredCities, setFilteredCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Debounced filtering - only runs when user stops typing
  const filterCities = useCallback((searchTerm) => {
    if (searchTerm.length < 2) {
      setFilteredCities([]);
      return;
    }

    setLoading(true);
    
    // Use setTimeout to prevent UI blocking
    setTimeout(() => {
      const filtered = cities.filter(city =>
        city.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCities(filtered);
      setLoading(false);
    }, 0);
  }, []);

  // Virtual scrolling component
  const ListboxComponent = React.forwardRef((props, ref) => {
    const { children, ...other } = props;
    const itemCount = Array.isArray(children) ? children.length : 0;
    const itemSize = 48;

    if (itemCount === 0) {
      return <div ref={ref} {...other} />;
    }

    return (
      <div ref={ref} {...other}>
        <List
          style={{ height: Math.min(itemCount * itemSize, 300), width: '100%' }}
          rowCount={itemCount}
          rowHeight={itemSize}
          overscanCount={5}
          rowProps={{}}
          rowComponent={({ index, style }) => (
            <div style={style}>
              {children[index]}
            </div>
          )}
        />
      </div>
    );
  });

  // Handle input changes with debouncing
  const handleInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
    filterCities(newInputValue);
  }, [filterCities]);

  // Handle autocomplete open/close
  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (inputValue.length >= 2) {
      filterCities(inputValue);
    }
  }, [inputValue, filterCities]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Default styling that matches your existing design
  const defaultSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: 'background.paper',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      fontFamily: '"Space Grotesk", Inter, sans-serif',
      '& fieldset': {
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
      },
      '&:hover': {
        boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
        '& fieldset': {
          borderColor: isDarkMode ? '#475569' : '#bdbdbd',
        },
      },
      '&.Mui-focused': {
        boxShadow: isDarkMode ? '0 8px 30px rgba(0, 0, 0, 0.4)' : '0 8px 30px rgba(0, 0, 0, 0.12)',
        '& fieldset': {
          borderColor: isDarkMode ? '#fff' : '#000',
        },
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'text.secondary',
      '&.Mui-focused': {
        color: 'text.primary',
      }
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      color: 'text.primary',
    },
    '& .MuiAutocomplete-listbox': {
      borderRadius: 2,
      boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
      border: isDarkMode ? '1px solid #334155' : '1px solid rgba(0, 0, 0, 0.08)',
      backgroundColor: 'background.paper',
      padding: 0,
      overflow: 'hidden',
      fontFamily: '"Space Grotesk", Inter, sans-serif',
    }
  };

  return (
    <Autocomplete
      options={filteredCities}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      onOpen={handleOpen}
      onClose={handleClose}
      open={isOpen}
      ListboxComponent={ListboxComponent}
      loading={loading}
      filterOptions={(x) => x} // Disable built-in filtering (we do it manually)
      getOptionLabel={(option) => option || ''}
      isOptionEqualToValue={(option, value) => option === value}
      noOptionsText={inputValue.length < 2 ? "Type at least 2 characters..." : "No cities found"}
      freeSolo
      sx={{ ...defaultSx, ...sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="City"
          placeholder="Start typing to search cities..."
          sx={{
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'text.secondary',
              '&.Mui-focused': {
                color: 'text.primary',
              }
            },
            '& .MuiInputBase-input': {
              fontSize: '0.875rem',
              color: 'text.primary',
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.75rem',
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option, { selected }) => (
        <li 
          {...props}
          style={{
            ...props.style,
            padding: '12px 16px',
            margin: 0,
            borderBottom: isDarkMode ? '1px solid #334155' : '1px solid rgba(0, 0, 0, 0.04)',
            background: selected 
              ? (isDarkMode ? '#334155' : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)')
              : 'transparent',
            color: isDarkMode ? theme.palette.text.primary : '#000',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            fontFamily: '"Space Grotesk", Inter, sans-serif',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = selected 
              ? (isDarkMode ? '#334155' : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)')
              : (isDarkMode ? '#1e293b' : 'linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%)');
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = selected 
              ? (isDarkMode ? '#334155' : 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)')
              : 'transparent';
          }}
        >
          {option}
        </li>
      )}
      {...props}
    />
  );
};

export default OptimizedCityAutocomplete;
